import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Snackbar,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Book as BookIcon,
  Chat as ChatIcon,
  Code as CodeIcon,
  Send as SendIcon,
  Save as SaveIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { Layout, StyledCard } from './common';
import AppearanceSettings from './widget/AppearanceSettings';
import { useAuth } from '../contexts/AuthContext';
import { apiService, WidgetConfig, DEFAULT_WIDGET_CONFIG } from '../services/api';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import * as pdfjsLib from 'pdfjs-dist';

interface CustomerData {
  id: string;
  name: string;
  email: string;
  phone: string;
  createdAt: string;
  isActive: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserDashboard: React.FC = () => {
  const { logout, username, currentUser, userRole } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig>(DEFAULT_WIDGET_CONFIG);
  const [contextText, setContextText] = useState('');
  const [chatMessages, setChatMessages] = useState<{role: string, content: string}[]>([]);
  const [chatMessage, setChatMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [urlsText, setUrlsText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [simOpen, setSimOpen] = useState(false);
  const [customerApiKey, setCustomerApiKey] = useState('');
  const [simApiKeyOverride, setSimApiKeyOverride] = useState('');

  // Configure PDF.js worker (served from public/ for reliability)
  // @ts-ignore
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

  useEffect(() => {
    console.log('🔍 [UserDashboard] useEffect triggered - checking auth state');
    console.log('🔍 [UserDashboard] currentUser:', currentUser);
    console.log('🔍 [UserDashboard] userRole:', userRole);
    
    if (currentUser && userRole?.customerId) {
      console.log('🔍 [UserDashboard] Found customerId in userRole:', userRole.customerId);
      loadCustomerData(userRole.customerId);
    } else if (currentUser && userRole?.role === 'customer' && !userRole.customerId) {
      console.log('⚠️ [UserDashboard] Customer user but no customerId in claims, may need to wait for claims update');
      // Set a timeout to retry as Firebase claims might still be updating
      setTimeout(() => {
        if (userRole?.customerId) {
          loadCustomerData(userRole.customerId);
        } else {
          console.log('❌ [UserDashboard] Still no customerId after timeout');
          setErrorMessage('Customer ID not found. Please contact support.');
          setInitialLoading(false);
        }
      }, 2000);
    } else {
      console.log('❌ [UserDashboard] No customer ID available');
      setErrorMessage('Customer profile not found. Please contact support.');
      setInitialLoading(false);
    }
  }, [currentUser, userRole]);

  const loadCustomerData = async (customerId: string) => {
    console.log('🔍 [UserDashboard] Loading customer data for ID:', customerId);
    setInitialLoading(true);
    
    try {
      const fullCustomerData = await apiService.getCustomerById(customerId);
      console.log('🔍 [UserDashboard] Full customer data from API:', fullCustomerData);
      
      if (fullCustomerData) {
        // Convert API customer data to our local interface
        const customerData: CustomerData = {
          id: fullCustomerData.id,
          name: fullCustomerData.name,
          email: fullCustomerData.email || '',
          phone: '', // Not available in backend data
          createdAt: fullCustomerData.created_at,
          isActive: fullCustomerData.status === 'active'
        };
        
        console.log('✅ [UserDashboard] Setting customer data:', customerData);
        setCustomerData(customerData);
        setCustomerApiKey(fullCustomerData.api_key || '');
        
        // Load widget config
        if (fullCustomerData.widget_config) {
          console.log('✅ [UserDashboard] Setting widget config:', fullCustomerData.widget_config);
          setWidgetConfig(fullCustomerData.widget_config);
        } else {
          console.log('⚠️ [UserDashboard] No widget config found, using default');
          setWidgetConfig(DEFAULT_WIDGET_CONFIG);
        }
        
        // Load context
        await loadContextText(customerId);

        // Load URLs from Firestore
        try {
          const ref = doc(db, 'customers', customerId);
          const snap = await getDoc(ref);
          const urls: string[] = (snap.exists() && (snap.data() as any).urls) || [];
          setUrlsText(urls.join(', '));
        } catch (e) {
          console.warn('URLs not found in Firestore yet.');
        }
      } else {
        console.log('❌ [UserDashboard] No customer data returned from API');
        setErrorMessage('Customer profile not found.');
      }
    } catch (error) {
      console.error('❌ [UserDashboard] Error loading customer data:', error);
      setErrorMessage('Failed to load customer profile. Please try again.');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSaveUrls = async () => {
    if (!customerData?.id) return;
    try {
      setLoading(true);
      const ref = doc(db, 'customers', customerData.id);
      const urls = urlsText
        .split(',')
        .map(u => u.trim())
        .filter(u => u.length > 0);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        await updateDoc(ref, { urls });
      } else {
        await setDoc(ref, { urls }, { merge: true });
      }
      setSuccessMessage('URLs saved successfully!');
    } catch (e) {
      setErrorMessage('Failed to save URLs');
    } finally {
      setLoading(false);
    }
  };

  const loadContextText = async (customerId: string) => {
    console.log('🔍 [UserDashboard] Loading context for customer ID:', customerId);
    
    try {
      const context = await apiService.getUserContext(customerId);
      console.log('🔍 [UserDashboard] Context received from API:', context);
      setContextText(context || '');
    } catch (error) {
      console.error('❌ [UserDashboard] Error loading context:', error);
      // Don't show error for context loading failure, just use empty context
    }
  };

  const handleSaveWidgetConfig = async () => {
    console.log('🔍 [UserDashboard] Saving widget config for customer:', customerData?.id);
    console.log('🔍 [UserDashboard] Widget config to save:', widgetConfig);
    
    if (!customerData?.id) {
      setErrorMessage('Customer ID not available');
      return;
    }
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      await apiService.updateUserWidgetConfig(customerData.id, widgetConfig);
      console.log('✅ [UserDashboard] Widget config saved successfully');
      setSuccessMessage('Widget configuration saved successfully!');
    } catch (error: any) {
      console.error('❌ [UserDashboard] Error saving widget config:', error);
      setErrorMessage('Failed to save widget configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContext = async () => {
    console.log('🔍 [UserDashboard] Saving context for customer:', customerData?.id);
    console.log('🔍 [UserDashboard] Context to save:', contextText);
    
    if (!customerData?.id) {
      setErrorMessage('Customer ID not available');
      return;
    }
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      await apiService.updateUserContext(customerData.id, contextText);
      console.log('✅ [UserDashboard] Context saved successfully');
      setSuccessMessage('Context updated successfully!');
    } catch (error: any) {
      console.error('❌ [UserDashboard] Error saving context:', error);
      setErrorMessage('Failed to save context');
    } finally {
      setLoading(false);
    }
  };

  const extractTextFromPdf = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    try {
      // @ts-ignore - pdfjs types are broad in dist bundle
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      // @ts-ignore
      const pdf = await loadingTask.promise;
      let fullText = '';
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        // @ts-ignore
        const page = await pdf.getPage(pageNum);
        // @ts-ignore
        const content = await page.getTextContent({ normalizeWhitespace: true });
        let pageText = '';
        const items: any[] = (content as any).items || [];
        for (const item of items) {
          const text = (item && typeof item === 'object' && 'str' in item) ? (item as any).str : '';
          const hasEOL = (item && typeof item === 'object' && 'hasEOL' in item) ? Boolean((item as any).hasEOL) : false;
          pageText += text + (hasEOL ? '\n' : ' ');
        }
        fullText += pageText.trim() + '\n\n';
      }
      return fullText.trim();
    } catch (err) {
      throw err;
    }
  };

  const handlePdfUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handlePdfFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    try {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.type !== 'application/pdf') {
        setErrorMessage('Please upload a PDF file.');
        return;
      }
      setLoading(true);
      const arrayBuffer = await file.arrayBuffer();
      const text = await extractTextFromPdf(arrayBuffer);
      setContextText((prev) => `${prev}${prev ? '\n\n' : ''}---\nAppended from ${file.name}:\n\n${text}`);
      setSuccessMessage('PDF content appended. Remember to Save Context.');
    } catch (error) {
      setErrorMessage('Failed to extract text from PDF');
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTestChat = async () => {
    // 1. Do nothing if the input is empty
    if (!customerData?.id || !chatMessage.trim()) return;

    // 2. Prepare the new user message
    const userMessage = { role: 'user', content: chatMessage.trim() };

    // 3. IMPORTANT: Create the new, complete conversation history BEFORE the API call
    const updatedMessages = [...chatMessages, userMessage];

    // 4. Update the UI immediately so the user sees their message
    setChatMessages(updatedMessages);
    setChatMessage(''); // Clear the input field

    try {
      // 5. CRITICAL FIX: Send the ENTIRE `updatedMessages` array to the backend
      // The payload is now an object: { messages: [...] }
      const response = await apiService.testCustomerChat(
        customerData.id,
        { messages: updatedMessages } 
      );

      // 6. Create the assistant's message object from the response
      const assistantMessage = { 
        role: 'assistant', 
        content: response.response // Make sure your apiService returns the data correctly
      };

      // 7. Update the UI with the AI's final response
      setChatMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('❌ [UserDashboard] Error testing chat:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, an error occurred while getting a response.'
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  const generateSimulatorSrcDoc = (): string => {
    const apiKey = (simApiKeyOverride && simApiKeyOverride.trim()) || customerApiKey || 'your-api-key';
    const cfg = widgetConfig;
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>html,body{height:100%;margin:0}#root{height:100%}</style>
  </head>
  <body>
    <div id="root"></div>
    <script>
      window.chatWidgetConfig = {
        apiKey: ${JSON.stringify(apiKey)},
        apiEndpoint: ${JSON.stringify(cfg.apiEndpoint)},
        title: ${JSON.stringify(cfg.title)},
        subtitle: ${JSON.stringify(cfg.subtitle)},
        primaryColor: ${JSON.stringify(cfg.primaryColor)},
        secondaryColor: ${JSON.stringify(cfg.secondaryColor)},
        welcomeMessage: ${JSON.stringify(cfg.welcomeMessage)},
        temperature: ${JSON.stringify(cfg.temperature)},
        maxTokens: ${JSON.stringify(cfg.max_output_tokens)},
        model: ${JSON.stringify(cfg.model)}
      };
    </script>
    <script src="https://cdn.jsdelivr.net/gh/MohammedRashad/jackbot-widget@0.5/widget.js"></script>
  </body>
</html>`;
  };

  const generateScriptCode = () => {
    if (!customerData?.id) return '';
    
    return `<!-- Add this before closing </body> tag -->
<script>
    window.chatWidgetConfig = {
        apiKey: "your-api-key", 
        apiEndpoint: '${widgetConfig.apiEndpoint}',
        title: '${widgetConfig.title}',
        subtitle: '${widgetConfig.subtitle}',
        primaryColor: '${widgetConfig.primaryColor}',
        secondaryColor: '${widgetConfig.secondaryColor}',
        welcomeMessage: \`${JSON.stringify(widgetConfig.welcomeMessage).slice(1, -1)}\`,
        temperature: ${widgetConfig.temperature},
        maxTokens: ${widgetConfig.max_output_tokens},
        model: '${widgetConfig.model}'
    };
</script>
<script src="https://cdn.jsdelivr.net/gh/MohammedRashad/jackbot-widget@0.5/widget.js"></script>`;
  };

  // Show loading while fetching initial data
  if (initialLoading) {
    return (
      <Layout
        title="Loading..."
        username={username || undefined}
        onLogout={logout}
        onProfile={() => navigate('/profile')}
      >
        <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading your dashboard...
          </Typography>
        </Container>
      </Layout>
    );
  }

  // Show error if no customer data
  if (!customerData) {
    return (
      <Layout
        title="Dashboard Error"
        username={username || undefined}
        onLogout={logout}
        onProfile={() => navigate('/profile')}
      >
        <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
          <Alert severity="error" sx={{ borderRadius: 2, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Unable to load customer profile
            </Typography>
            <Typography variant="body2">
              {errorMessage || 'Please contact support or check your account settings.'}
            </Typography>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout
      title="Jackbot Customer Dashboard"
      username={customerData.name}
      onLogout={logout}
      onProfile={() => navigate('/profile')}
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Success/Error Messages */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage('')}
        >
          <Alert
            onClose={() => setSuccessMessage('')}
            severity="success"
            variant="filled"
          >
            {successMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={() => setErrorMessage('')}
        >
          <Alert
            onClose={() => setErrorMessage('')}
            severity="error"
            variant="filled"
          >
            {errorMessage}
          </Alert>
        </Snackbar>

        {/* Welcome Card */}
        <StyledCard sx={{ mb: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>
              Welcome back, {customerData.name}!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your chatbot configuration and test its functionality
            </Typography>
          </Box>
        </StyledCard>

        {/* Main Content Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="dashboard tabs"
          >
            <Tab icon={<PaletteIcon />} label="Widget Settings" />
            <Tab icon={<BookIcon />} label="Knowledge Base" />
            <Tab icon={<ChatIcon />} label="Test Chat" />
            <Tab icon={<CodeIcon />} label="Integration" />
          </Tabs>

          {/* Widget Settings Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <StyledCard title="Basic Settings">
                  <TextField
                    fullWidth
                    label="Widget Title"
                    value={widgetConfig.title}
                    onChange={(e) => setWidgetConfig(prev => ({ ...prev, title: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Widget Subtitle"
                    value={widgetConfig.subtitle}
                    onChange={(e) => setWidgetConfig(prev => ({ ...prev, subtitle: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Welcome Message"
                    value={widgetConfig.welcomeMessage}
                    onChange={(e) => setWidgetConfig(prev => ({ ...prev, welcomeMessage: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                </StyledCard>
              </Box>
              <Box sx={{ flex: 1 }}>
                <StyledCard title="Appearance">
                  <AppearanceSettings
                    primaryColor={widgetConfig.primaryColor}
                    secondaryColor={widgetConfig.secondaryColor}
                    onChange={(next) => setWidgetConfig(prev => ({ ...prev, ...next }))}
                  />
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveWidgetConfig}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                      size="large"
                    >
                      {loading ? 'Saving...' : 'Save Configuration'}
                    </Button>
                  </Box>
                </StyledCard>
              </Box>
            </Box>
          </TabPanel>

          {/* Knowledge Base Tab */}
          <TabPanel value={activeTab} index={1}>
            <StyledCard 
              title="AI Knowledge Base"
              subtitle="Provide context and information for your chatbot to use in conversations"
            >
              <input
                type="file"
                accept="application/pdf"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handlePdfFileChange}
              />
              <TextField
                fullWidth
                multiline
                rows={12}
                label="Context & Knowledge"
                value={contextText}
                onChange={(e) => setContextText(e.target.value)}
                placeholder="Enter information about your business, FAQs, policies, or any other context your chatbot should know..."
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Comma-separated URLs"
                value={urlsText}
                onChange={(e) => setUrlsText(e.target.value)}
                placeholder="https://example.com/a, https://example.com/b"
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Character count: {contextText.length.toLocaleString()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleSaveUrls}
                    disabled={loading}
                  >
                    Save URLs
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handlePdfUploadClick}
                    disabled={loading}
                    startIcon={<UploadFileIcon />}
                  >
                    Upload PDF
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveContext}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  >
                    {loading ? 'Saving...' : 'Save Context'}
                  </Button>
                </Box>
              </Box>
            </StyledCard>
          </TabPanel>

          {/* Test Chat Tab */}
          <TabPanel value={activeTab} index={2}>
            <StyledCard title="Chat Testing Interface">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  API Endpoint: {widgetConfig.apiEndpoint} | API Key: {customerApiKey ? `${customerApiKey.slice(0,4)}…${customerApiKey.slice(-4)}` : 'not set'} | Status: {customerData.isActive ? 'Active' : 'Inactive'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField size="small" label="Simulator API Key (optional)" value={simApiKeyOverride} onChange={(e) => setSimApiKeyOverride(e.target.value)} sx={{ minWidth: 260 }} />
                  <Button 
                    variant="outlined" 
                    onClick={() => setSimOpen(true)}
                    disabled={!customerData.isActive || (!customerApiKey && !simApiKeyOverride)}
                  >
                    Open Widget Simulator
                  </Button>
                </Box>
              </Box>
              <Box sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ 
                  flexGrow: 1, 
                  overflow: 'auto', 
                  p: 2, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  borderRadius: 1, 
                  mb: 2,
                  bgcolor: 'grey.50'
                }}>
                  {chatMessages.length === 0 ? (
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ textAlign: 'center', py: 8 }}
                    >
                      Start a conversation to test your chatbot
                    </Typography>
                  ) : (
                    chatMessages.map((message, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: message.role === 'user' ? 'primary.light' : 'background.paper',
                          color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                          maxWidth: '80%',
                          ml: message.role === 'user' ? 'auto' : 0,
                          boxShadow: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {message.content}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Type your message..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleTestChat();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleTestChat}
                    disabled={!chatMessage.trim()}
                    endIcon={<SendIcon />}
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            </StyledCard>
            <Dialog open={simOpen} onClose={() => setSimOpen(false)} fullWidth maxWidth="md">
              <DialogTitle>Widget Simulator</DialogTitle>
              <DialogContent dividers>
                <iframe
                  title="Widget Simulator"
                  style={{ width: '100%', height: 600, border: '0' }}
                  srcDoc={generateSimulatorSrcDoc()}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSimOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </TabPanel>

          {/* Integration Tab */}
          <TabPanel value={activeTab} index={3}>
            <StyledCard 
              title="Widget Integration Code"
              subtitle="Copy and paste this code into your website before the closing </body> tag"
            >
              <Paper 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.900', 
                  color: 'grey.100',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  overflow: 'auto',
                  maxHeight: 300
                }}
              >
                <pre>{generateScriptCode()}</pre>
              </Paper>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Note:</strong> Replace "your-api-key" with your actual API key from the customer dashboard.
                </Typography>
              </Box>
            </StyledCard>
          </TabPanel>
        </Paper>
      </Container>
    </Layout>
  );
};

export default UserDashboard; 